// Self-serve service activation — the customer provisions a new provider service
// from the Services catalog. Backed by the v3 service API:
//
//   GET  /providers?per_page=100   → registerable providers (with status)
//   POST /services                 → create the service
//
// One call provisions everything: the backend resolves the customer org from the
// JWT, auto-binds the provider's default zone, and inserts the Service + zone in
// a single transaction. The caller passes only service-level info (name + the
// account email used for owner-team + upstream tenant); zone is NOT caller-chosen.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { foundationApi, serviceApiV3 } from '@/api/client';
import { serviceKeys } from '@/api/services';

interface Envelope<T> {
  status?: number;
  data: T;
  message?: string;
}

/** A registerable provider in the v3 catalog (animal-coded). */
export interface ProviderV3 {
  id: string;
  name: string; // animal key, e.g. "Eagle"
  display_name: string; // "Eagle CDN"
  type: string; // category: "cdn", "compute", "security"…
  status: 'active' | 'beta' | 'coming_soon';
  legacy_scope: string; // e.g. "External.SwiftFederation"
  description?: string;
  features?: string[];
}

interface ProvidersEnvelope {
  providers: ProviderV3[];
  total: number;
  page: number;
  per_page: number;
}

export const provisioningKeys = {
  providers: ['v3', 'providers', 'list'] as const,
};

/** All registerable providers in the catalog. */
export function useProviders() {
  return useQuery<ProviderV3[]>({
    queryKey: provisioningKeys.providers,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const res = await foundationApi.get<Envelope<ProvidersEnvelope>>('/providers', {
        params: { per_page: 100 },
      });
      return res.data?.data?.providers ?? [];
    },
  });
}

export interface ActivateServiceInput {
  serviceName: string;
  providerId: string;
  /** Account email — required by the backend for owner-team + tenant minting. */
  email: string;
}

/** Newly created service (subset of the create response we rely on). */
interface CreatedService {
  id: string;
}

/**
 * Activation result: the new service id plus the one-time root S3 credentials
 * some providers (Wasabi/WACM) mint at account creation. The secret is returned
 * exactly once in this response and is never retrievable again, so the caller
 * must reveal it to the customer immediately.
 */
export interface ActivatedService {
  id: string;
  accessKeyId?: string;
  secretAccessKey?: string;
}

/**
 * Provision a service against a provider. A single POST does the whole job: the
 * backend resolves the org from the JWT, auto-binds the provider's default zone,
 * and inserts the Service + zone atomically. Invalidates the my-services cache so
 * the new service shows on the dashboard immediately.
 *
 * Surfaces the backend's friendly message on failure — notably 409 when the org
 * already holds a service for this provider.
 */
export function useActivateService() {
  const qc = useQueryClient();
  return useMutation<ActivatedService, Error, ActivateServiceInput>({
    mutationFn: async ({ serviceName, providerId, email }) => {
      try {
        // The one-time root key/secret ride alongside `data` at the envelope root.
        const created = await serviceApiV3.post<
          Envelope<CreatedService> & { access_key_id?: string; secret_access_key?: string }
        >('/services', {
          service_name: serviceName,
          provider_id: providerId,
          email,
        });
        const service = created.data?.data;
        if (!service?.id) throw new Error('Service creation did not return an id');
        return {
          id: service.id,
          accessKeyId: created.data?.access_key_id,
          secretAccessKey: created.data?.secret_access_key,
        };
      } catch (err) {
        const e = err as { response?: { data?: { message?: string; error?: string } } };
        const data = e.response?.data;
        // Backend wraps a friendly `message` plus a deeper `error` chain (e.g. the
        // upstream vendor rejection). Surface both so failures are diagnosable.
        const msg = [data?.message, data?.error].filter(Boolean).join(' — ');
        throw new Error(msg || 'Activation request failed');
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: serviceKeys.mine });
    },
  });
}
